'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useState } from 'react';
import { INITIAL_PAGE_NUMBER, MAX_ITEMS_PER_LIST } from '@shared/utils/constants/pagination';
import { blurActiveElement } from '@shared/utils/helpers/blur-active-element';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { ArrowIcon } from '../svgs';
import styles from './pagination.module.scss';
export type PaginationProps = {
  blurButtonsOnPageChange?: boolean;
  className?: string;
  label?: {
    nextPage: string;
    pagination: string;
    previousPage: string;
    totalItemsCount: string;
  };
  maxItemsPerPage?: number;
  onPageChange: (index: number, blockCondition: boolean, forward?: boolean) => void;
  totalItemsCount?: number;
} & ({
  currentPage: number;
} | {
  initialPage?: number;
});
export const Pagination = t0 => {
  const $ = _c(49);
  let blurButtonsOnPageChange;
  let className;
  let label;
  let onPageChange;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      blurButtonsOnPageChange,
      className,
      label,
      maxItemsPerPage: t1,
      onPageChange,
      totalItemsCount: t2,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = blurButtonsOnPageChange;
    $[2] = className;
    $[3] = label;
    $[4] = onPageChange;
    $[5] = rest;
    $[6] = t1;
    $[7] = t2;
  } else {
    blurButtonsOnPageChange = $[1];
    className = $[2];
    label = $[3];
    onPageChange = $[4];
    rest = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const maxItemsPerPage = t1 === undefined ? MAX_ITEMS_PER_LIST : t1;
  const totalItemsCount = t2 === undefined ? 0 : t2;
  const formatTranslationString = useFormatTranslationString();
  const isControlledComponent = "currentPage" in rest;
  const [innerCurrentPage, setInnerCurrentPage] = useState(isControlledComponent ? undefined : rest.initialPage || INITIAL_PAGE_NUMBER);
  const currentPage = (isControlledComponent ? rest.currentPage : innerCurrentPage) as number;
  const totalPagesCount = Math.ceil(totalItemsCount / maxItemsPerPage);
  const endIndex = Math.min(currentPage * maxItemsPerPage, totalItemsCount);
  const startIndex = Math.min((currentPage - 1) * maxItemsPerPage + 1, endIndex);
  let t3;
  if ($[8] !== isControlledComponent || $[9] !== onPageChange) {
    t3 = (index, blockCondition, forward) => {
      if (!isControlledComponent) {
        setInnerCurrentPage(index);
      }
      onPageChange(index, blockCondition, forward);
    };
    $[8] = isControlledComponent;
    $[9] = onPageChange;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handlePageChange = t3;
  let t4;
  if ($[11] !== blurButtonsOnPageChange || $[12] !== currentPage || $[13] !== handlePageChange) {
    t4 = () => {
      if (blurButtonsOnPageChange) {
        blurActiveElement();
      }
      handlePageChange(currentPage === 1 ? 1 : currentPage - 1, currentPage !== 1);
    };
    $[11] = blurButtonsOnPageChange;
    $[12] = currentPage;
    $[13] = handlePageChange;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handlePrevious = t4;
  let t5;
  if ($[15] !== blurButtonsOnPageChange || $[16] !== currentPage || $[17] !== handlePageChange || $[18] !== totalPagesCount) {
    t5 = () => {
      if (blurButtonsOnPageChange) {
        blurActiveElement();
      }
      handlePageChange(Number(currentPage + 1), currentPage !== totalPagesCount, true);
    };
    $[15] = blurButtonsOnPageChange;
    $[16] = currentPage;
    $[17] = handlePageChange;
    $[18] = totalPagesCount;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleNext = t5;
  let t6;
  if ($[20] !== endIndex || $[21] !== formatTranslationString || $[22] !== startIndex) {
    t6 = formatTranslationString("{startIndex} - {endIndex}", {
      endIndex,
      startIndex
    });
    $[20] = endIndex;
    $[21] = formatTranslationString;
    $[22] = startIndex;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const formattedItemRange = t6;
  let t7;
  if ($[24] !== formatTranslationString || $[25] !== label || $[26] !== totalItemsCount) {
    t7 = label?.totalItemsCount && formatTranslationString(label.totalItemsCount, {
      total: totalItemsCount
    });
    $[24] = formatTranslationString;
    $[25] = label;
    $[26] = totalItemsCount;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const formattedTotalItemsCount = t7;
  let t8;
  if ($[28] !== className) {
    t8 = classNames(styles.pagination, className);
    $[28] = className;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== formattedItemRange) {
    t9 = <span className={styles["pagination__item-range"]}>{formattedItemRange}</span>;
    $[30] = formattedItemRange;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== formattedTotalItemsCount || $[33] !== t9) {
    t10 = <div>{t9} {formattedTotalItemsCount}</div>;
    $[32] = formattedTotalItemsCount;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== currentPage || $[36] !== handleNext || $[37] !== handlePrevious || $[38] !== label?.nextPage || $[39] !== label?.pagination || $[40] !== label?.previousPage || $[41] !== maxItemsPerPage || $[42] !== totalItemsCount || $[43] !== totalPagesCount) {
    t11 = totalItemsCount > maxItemsPerPage && <nav aria-label={label?.pagination} className={styles["button-wrapper"]}><button aria-label={label?.previousPage} className={styles.button} disabled={currentPage === 1} onClick={handlePrevious} type="button"><ArrowIcon /></button><button aria-label={label?.nextPage} className={styles.button} disabled={currentPage === totalPagesCount} onClick={handleNext} type="button"><ArrowIcon className={styles["button__icon-next"]} /></button></nav>;
    $[35] = currentPage;
    $[36] = handleNext;
    $[37] = handlePrevious;
    $[38] = label?.nextPage;
    $[39] = label?.pagination;
    $[40] = label?.previousPage;
    $[41] = maxItemsPerPage;
    $[42] = totalItemsCount;
    $[43] = totalPagesCount;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t8) {
    t12 = <div className={t8}>{t10}{t11}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t8;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
};